import React from 'react';
import { QueryClient, useQuery } from '@tanstack/react-query';

import { ROVER_SEARCH_SOURCE_TYPE_UNKNOWN } from '@rover/constants/src/search/search.constants';
import { SeoSlugEnum } from '@rover/constants/src/search/seo.constants';
import makeSearchQuery from '@rover/react-lib/src/pages/landing/utilities/makeSearchQuery';
import {
  apiCitiesAndNeighborhoodsList,
  getApiCitiesAndNeighborhoodsListQueryKey,
  useApiCitiesAndNeighborhoodsList,
} from '@rover/rsdk/src/apiClient/latest/api-cities-and-neighborhoods-list';
import {
  apiCountryFooterLinksList,
  getApiCountryFooterLinksListQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-country-footer-links-list';
import {
  apiCountryRegionLinksList,
  getApiCountryRegionLinksListQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-country-region-links-list';
import {
  apiFrontendServiceTypesList,
  getApiFrontendServiceTypesListQueryKey,
  useApiFrontendServiceTypesList,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-service-types-list';
import { ApiSearchOptimizedRetrieveParams } from '@rover/rsdk/src/apiClient/latest/model/apiSearchOptimizedRetrieveParams';
import { ApiSearchOptimizedRetrievePetType } from '@rover/rsdk/src/apiClient/latest/model/apiSearchOptimizedRetrievePetType';
import { ApiServiceLinksRetrieve200DataItem } from '@rover/rsdk/src/apiClient/latest/model/apiServiceLinksRetrieve200DataItem';
import { PaginatedBrandLocalityServiceCitiesItemList } from '@rover/rsdk/src/apiClient/latest/model/paginatedBrandLocalityServiceCitiesItemList';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import useCitiesAndNeighborhoodsShim from '@rover/shared/js/hooks/useCitiesAndNeighborhoodsShim';
import useCountryConfigurationShim from '@rover/shared/js/hooks/useCountryConfigurationShim';
import useCountryFooterLinksShim from '@rover/shared/js/hooks/useCountryFooterLinksShim';
import useCountryRegionLinksShim from '@rover/shared/js/hooks/useCountryRegionLinksShim';
import useFrontendConfigurationShim from '@rover/shared/js/hooks/useFrontendConfigurationShim';
import useServiceLanding, {
  getServiceLandingFetchArgs,
} from '@rover/shared/js/hooks/useServiceLanding';
import useServiceLinksShim from '@rover/shared/js/hooks/useServiceLinksShim';
import { getPath, getURL } from '@rover/shared/js/utils/getFullUrl';
import getParam from '@rover/shared/js/utils/getParams';
import {
  isServiceLanding,
  SERVICE_TYPE_FOR_SERVICE_LANDING_ENUM,
  SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';
import {
  COUNTRY_URL_PREFIXES_REGEX_PATTERN,
  getCountryUrlPrefix,
  US_COUNTRY_URL_PREFIX,
} from '@rover/utilities/roverGeo.constants';
import { redirect } from '@rover/utilities/url';

import { SERVICE_LANDINGS_WITH_SEARCH_RESULTS } from './constants';
import ServiceLandingPage from './ServiceLandingPage';

const SERVICE_LANDING_REGEX = new RegExp(
  `^(?:/(?<country_url_prefix>${COUNTRY_URL_PREFIXES_REGEX_PATTERN}))?/(?<service_landing_slug>[a-z-]+)/`
);

const getServiceLandingType = (
  serviceLinks: ApiServiceLinksRetrieve200DataItem[],
  path: string
): ServiceLandingEnum | null => {
  if (!serviceLinks) {
    captureError(new Error('Service links routing data is not available'));
    return null;
  }

  const serviceLink = serviceLinks.find(({ href }) => href && path.startsWith(href));

  if (!serviceLink) {
    captureError(new Error(`Could not match path "${path}" to a valid service landing page`));
    return null;
  }

  if (!isServiceLanding(serviceLink.type)) {
    captureError(
      new TypeError(
        `Found a service landing page for "${path}", but "${serviceLink.type}" is not a valid type`
      )
    );
    return null;
  }

  return serviceLink.type;
};

const getCitiesAndNeighborhoods = (
  citiesAndNeighborhoods: PaginatedBrandLocalityServiceCitiesItemList
): Array<{ title: string; href: string }> => {
  return (citiesAndNeighborhoods?.results ?? [])
    .filter((c) => c.city && c.seoSearchPath)
    .map((c) => ({
      title: c.city!,
      href: c.seoSearchPath!,
    }));
};

const getServiceLandingSlug = (path: string): string => {
  const translatedServiceLandingSlug =
    SERVICE_LANDING_REGEX.exec(path)?.groups?.service_landing_slug;

  if (!translatedServiceLandingSlug) {
    throw new Error(`Could not parse service landing slug from path: ${path}`);
  }
  return translatedServiceLandingSlug;
};

const ServiceLandingPageContainer: React.FC & { reactQueryPrefetch: any } = () => {
  const path = getPath();
  // always redirect `/us/*`
  if (getCountryUrlPrefix(path) === US_COUNTRY_URL_PREFIX) {
    // same exact location, just trim off the leading bit
    const location = getURL();
    location.pathname = location.pathname.slice('/us'.length);
    redirect(location);
  }

  const serviceLandingSlug = getServiceLandingSlug(path);
  const countryUrlPrefix = getCountryUrlPrefix(path) || US_COUNTRY_URL_PREFIX;

  const { language = 'en-us', settings } = useFrontendConfigurationShim();
  const { serviceLinksForCurrentRoute, serviceLinks, serviceLinksWithDisplayName } =
    useServiceLinksShim();
  const { navigation: countryConfig } = useCountryConfigurationShim();

  const { data: serviceLandingData } = useServiceLanding(countryUrlPrefix, serviceLandingSlug);

  const { results: countryFooterLinks } = useCountryFooterLinksShim();
  const countryRegionLinks = useCountryRegionLinksShim();
  const { results: citiesAndNeighborhoodsLinks } = useCitiesAndNeighborhoodsShim(
    (serviceLandingData?.serviceLandingUrlSlug as SeoSlugEnum) || SeoSlugEnum.DOG_BOARDING
  );

  const serviceType = serviceLandingData?.serviceLandingUrlSlug
    ? SERVICE_TYPE_FOR_SERVICE_LANDING_ENUM[serviceLandingData.serviceLandingUrlSlug]
    : undefined;

  const { data: citiesAndNeighborhoods } = useApiCitiesAndNeighborhoodsList(
    // API expects the seo slug, not the internal service type (i.e. "pet-sitting" not "overnight-boarding")
    { countryUrlPrefix, serviceType: serviceLandingData?.serviceLandingUrlSlug },
    {
      query: { enabled: !!serviceLandingData?.serviceLandingUrlSlug },
    }
  );

  const serviceLandingType = serviceLinks ? getServiceLandingType(serviceLinks, path) : null;

  const shouldFetchSearchResults =
    serviceLandingType !== null &&
    !!serviceType &&
    SERVICE_LANDINGS_WITH_SEARCH_RESULTS.includes(serviceLandingType);

  const searchOptimizedRetrieveParams: ApiSearchOptimizedRetrieveParams = {
    serviceType,
    ...(serviceLandingData?.serviceLandingUrlSlug &&
    ['cat-boarding', 'cat-sitting'].includes(serviceLandingData.serviceLandingUrlSlug)
      ? { petType: ApiSearchOptimizedRetrievePetType.cat }
      : {}),
  };

  const { data: searchResults } = useQuery(
    makeSearchQuery(searchOptimizedRetrieveParams, {
      enabled: shouldFetchSearchResults,
      searchSourceType: ROVER_SEARCH_SOURCE_TYPE_UNKNOWN,
    })
  );

  const { data: serviceTypes } = useApiFrontendServiceTypesList();

  return (
    serviceLinksForCurrentRoute &&
    countryConfig &&
    serviceLandingData &&
    citiesAndNeighborhoods?.results &&
    serviceLinks &&
    serviceLinksWithDisplayName &&
    settings &&
    serviceTypes &&
    countryRegionLinks && (
      <ServiceLandingPage
        serviceLandingType={getServiceLandingType(serviceLinks, path)}
        serviceLinks={serviceLinksForCurrentRoute}
        serviceLinksWithDisplayName={serviceLinksWithDisplayName.filter(({ href }) => {
          return (getCountryUrlPrefix(href) ?? US_COUNTRY_URL_PREFIX) === countryUrlPrefix;
        })}
        language={language}
        countryConfig={countryConfig}
        serviceLandingData={serviceLandingData}
        tags={getParam('tags')}
        citiesAndNeighborhoods={getCitiesAndNeighborhoods(citiesAndNeighborhoods)}
        serviceTypeSlug={
          SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM[serviceLandingData.serviceLandingUrlSlug]
        }
        serviceTypes={serviceTypes}
        searchResults={searchResults}
        frontendConfigSettings={settings}
        citiesAndNeighborhoodsLinks={citiesAndNeighborhoodsLinks}
        countryFooterLinks={countryFooterLinks}
        countryRegionLinks={countryRegionLinks}
      />
    )
  );
};

ServiceLandingPageContainer.reactQueryPrefetch = async (queryClient: QueryClient) => {
  const path = getPath();
  const countryUrlPrefix = getCountryUrlPrefix(path) || US_COUNTRY_URL_PREFIX;
  const serviceLandingSlug = getServiceLandingSlug(path);

  const serviceLandingRetrieveArgs = getServiceLandingFetchArgs(
    countryUrlPrefix,
    serviceLandingSlug
  );

  const serviceLandingData = await queryClient.fetchQuery(serviceLandingRetrieveArgs);

  return Promise.all([
    queryClient.prefetchQuery({
      queryKey: getApiCitiesAndNeighborhoodsListQueryKey({
        countryUrlPrefix,
        serviceType: serviceLandingData?.serviceLandingUrlSlug,
      }),
      queryFn: () =>
        apiCitiesAndNeighborhoodsList({
          countryUrlPrefix,
          serviceType: serviceLandingData?.serviceLandingUrlSlug,
        }),
    }),
    queryClient.prefetchQuery({
      queryKey: getApiCountryFooterLinksListQueryKey({
        countryUrlPrefix,
      }),
      queryFn: () =>
        apiCountryFooterLinksList({
          countryUrlPrefix,
        }),
    }),
    queryClient.prefetchQuery({
      queryKey: getApiCountryRegionLinksListQueryKey({
        countryUrlPrefix,
      }),
      queryFn: () =>
        apiCountryRegionLinksList({
          countryUrlPrefix,
        }),
    }),
    // add prefetches for region & country links
    queryClient.prefetchQuery({
      queryKey: getApiFrontendServiceTypesListQueryKey(),
      queryFn: () => apiFrontendServiceTypesList(),
    }),
  ]);
};

export default ServiceLandingPageContainer;
